import React, { useState, useRef, useEffect, useCallback } from "react";
import { format } from "@flasd/whatsapp-formatting";
import { Icon } from "../UIElements/Icon";
import { Button } from "../UIElements/Button";
import { useTranslation } from "react-i18next";
import {
  useConversationMessages,
  useConversationRoom,
  useConversationMessagesSocket,
} from "@finitless/shared/hooks";
import { useGlobalUtils } from "../../../lib/globalUtils";

const ConversationChat = ({
  conversationId,
  className = "",
  showScrollButton = true,
  enablePagination = true,
  maxHeight = "h-96",
  customMessageRenderer = null,
}) => {
  const { t } = useTranslation("conversations");
  const { parseDate } = useGlobalUtils();

  const [allMessages, setAllMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showScrollButtonState, setShowScrollButtonState] = useState(false);

  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const postScrollTimeoutRef = useRef(null);
  const previousConversationIdRef = useRef(conversationId);
  const scrollStateRef = useRef({
    isPaginating: false,
    previousScrollHeight: 0,
  });
  const lastMessageIdRef = useRef(null);

  const MESSAGES_PER_PAGE = 50;

  // Setup socket listeners
  useConversationMessagesSocket(conversationId, {
    params:
      "id,message,role,created_at,is_public,is_ai_relevant,label,is_flagged",
    orderBy: "-created_at",
    page: currentPage,
    page_size: MESSAGES_PER_PAGE,
  });

  useConversationRoom(conversationId);

  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
  } = useConversationMessages(conversationId, {
    params:
      "id,message,role,created_at,is_public,is_ai_relevant,label,is_flagged",
    orderBy: "-created_at",
    page: currentPage,
    page_size: MESSAGES_PER_PAGE,
  });

  const pagination = messagesData?.pagination || {};

  // Handle messages data updates
  useEffect(() => {
    if (messagesData?.messages) {
      const newMessages = [...messagesData.messages];

      if (scrollStateRef.current.isPaginating && messagesContainerRef.current) {
        scrollStateRef.current.previousScrollHeight =
          messagesContainerRef.current.scrollHeight;
      }

      setAllMessages((prevMessages) => {
        const messageMap = new Map();
        prevMessages.forEach((msg) => messageMap.set(msg.id, msg));
        newMessages.forEach((msg) => messageMap.set(msg.id, msg));

        const sortedMessages = Array.from(messageMap.values()).sort(
          (a, b) => a.id - b.id,
        );

        if (sortedMessages.length > 0) {
          lastMessageIdRef.current =
            sortedMessages[sortedMessages.length - 1].id;
        }

        return sortedMessages;
      });

      const totalPages = pagination.total_pages || 1;
      setHasMoreMessages(currentPage < totalPages);
      setIsLoadingMore(false);
      scrollStateRef.current.isPaginating = false;
    }
  }, [messagesData, currentPage, pagination.total_pages]);

  // Handle new messages from socket
  useEffect(() => {
    const lastMessage =
      allMessages.length > 0 ? allMessages[allMessages.length - 1] : null;
    const isNewMessageFromSocket =
      lastMessage && lastMessage.id !== lastMessageIdRef.current;

    if (isNewMessageFromSocket) {
      lastMessageIdRef.current = lastMessage.id;
    }
  }, [allMessages]);

  // Handle scroll position after pagination
  useEffect(() => {
    if (
      scrollStateRef.current.isPaginating &&
      scrollStateRef.current.previousScrollHeight > 0 &&
      messagesContainerRef.current
    ) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      const scrollDifference =
        newScrollHeight - scrollStateRef.current.previousScrollHeight;
      messagesContainerRef.current.scrollTop = scrollDifference;
      scrollStateRef.current.previousScrollHeight = 0;
      scrollStateRef.current.isPaginating = false;
    }
  }, [allMessages]);

  // Auto-scroll to bottom for new messages
  useEffect(() => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }

    if (postScrollTimeoutRef.current) {
      clearTimeout(postScrollTimeoutRef.current);
      postScrollTimeoutRef.current = null;
    }

    const lastMessage =
      allMessages.length > 0 ? allMessages[allMessages.length - 1] : null;
    const isInitialLoad =
      currentPage === 1 && !isLoadingMore && allMessages.length > 0;
    const isNewMessageAdded =
      lastMessage && lastMessage.id !== lastMessageIdRef.current;

    if (isInitialLoad || isNewMessageAdded) {
      const container = messagesContainerRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;

      setShowScrollButtonState(scrollTop + clientHeight < scrollHeight - 150);

      const shouldAutoScroll =
        isInitialLoad || (isNewMessageAdded && isNearBottom);

      if (shouldAutoScroll) {
        autoScrollTimeoutRef.current = setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: isInitialLoad ? "auto" : "smooth",
              block: "end",
            });
            postScrollTimeoutRef.current = setTimeout(() => {
              if (container) {
                const {
                  scrollTop: newScrollTop,
                  scrollHeight: newScrollHeight,
                  clientHeight: newClientHeight,
                } = container;
                setShowScrollButtonState(
                  newScrollTop + newClientHeight < newScrollHeight - 150,
                );
              }
            }, 150);
          }
        }, 100);
      }
    }

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }

      if (postScrollTimeoutRef.current) {
        clearTimeout(postScrollTimeoutRef.current);
        postScrollTimeoutRef.current = null;
      }
    };
  }, [allMessages, currentPage, isLoadingMore]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!enablePagination) return;

    const container = messagesContainerRef.current;
    if (!container || isLoadingMore || !hasMoreMessages) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    setShowScrollButtonState(scrollTop + clientHeight < scrollHeight - 150);

    if (scrollTop < 50 && !isLoadingMore) {
      scrollStateRef.current.isPaginating = true;
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  }, [isLoadingMore, hasMoreMessages, enablePagination]);

  const messagesContainerCallbackRef = useCallback(
    (node) => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.removeEventListener(
          "scroll",
          handleScroll,
        );
      }
      if (node) {
        node.addEventListener("scroll", handleScroll);
      }
      messagesContainerRef.current = node;
    },
    [handleScroll],
  );

  // Reset local pagination/message state when conversation changes
  useEffect(() => {
    if (previousConversationIdRef.current === conversationId) {
      return;
    }

    previousConversationIdRef.current = conversationId;
    setAllMessages([]);
    setCurrentPage(1);
    setHasMoreMessages(true);
    setIsLoadingMore(false);
    setShowScrollButtonState(false);
    lastMessageIdRef.current = null;
    scrollStateRef.current = { isPaginating: false, previousScrollHeight: 0 };
  }, [conversationId]);

  useEffect(() => {
    return () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.removeEventListener(
          "scroll",
          handleScroll,
        );
      }

      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }

      if (postScrollTimeoutRef.current) {
        clearTimeout(postScrollTimeoutRef.current);
        postScrollTimeoutRef.current = null;
      }
    };
  }, [handleScroll]);

  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        block: "end",
      });
    }
  };

  const formatTimestamp = (timestamp) =>
    parseDate(timestamp, {
      formatTime: "12",
      outputFormat: "MMM d, h:mm a",
      fallbackText: t("chat.fallbackNoTime"),
    });

  const parseToolMessage = (content) => {
    const successMatch = content.match(/<success\s+([^>]*)>(.*?)<\/success>/s);
    const errorMatch = content.match(/<error\s+([^>]*)>(.*?)<\/error>/s);

    if (successMatch) {
      const [, attributes, message] = successMatch;
      const attrs = {};
      const attrRegex = /(\w+)="([^"]*)"/g;
      let match;
      while ((match = attrRegex.exec(attributes))) {
        attrs[match[1]] = match[2];
      }
      return {
        type: "success",
        message: message.trim(),
        attributes: attrs,
      };
    }

    if (errorMatch) {
      const [, attributes, message] = errorMatch;
      const attrs = {};
      const attrRegex = /(\w+)="([^"]*)"/g;
      let match;
      while ((match = attrRegex.exec(attributes))) {
        attrs[match[1]] = match[2];
      }
      return {
        type: "error",
        message: message.trim(),
        attributes: attrs,
      };
    }

    return {
      type: "raw",
      message: content,
      attributes: {},
    };
  };

  const renderMessage = (message) => {
    if (customMessageRenderer) {
      return customMessageRenderer(message);
    }

    const {
      id,
      message: content,
      role,
      created_at,
      is_flagged,
      group_rank,
    } = message;
    const timestamp = formatTimestamp(created_at);
    const formattedContent = format(content);

    if (role === "user") {
      return (
        <div key={id} className="mb-2 flex justify-start px-4">
          <div className="max-w-xs lg:max-w-md">
            <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-2 shadow-sm">
              <div className="mb-1 flex items-start space-x-2">
                <Icon
                  name="User"
                  className="text-primary mt-0.5 h-4 w-4 flex-shrink-0"
                />
                <div
                  className="flex-1 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formattedContent }}
                />
              </div>
              <div className="mt-1 flex items-center justify-end">
                <span className="text-muted-foreground text-xs">
                  {timestamp}
                </span>
                {group_rank && (
                  <span className="text-muted-foreground ml-2 text-xs">
                    #{group_rank}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (role === "assistant") {
      return (
        <div key={id} className="mb-2 flex justify-end px-4">
          <div className="max-w-xs lg:max-w-md">
            <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-2 shadow-sm">
              <div className="mb-1 flex items-start space-x-2">
                <div
                  className="flex-1 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formattedContent }}
                />
                <Icon
                  name="Bot"
                  className="text-primary-foreground mt-0.5 h-4 w-4 flex-shrink-0"
                />
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-primary-foreground/75 text-xs">
                  {timestamp}
                </span>
                <div className="flex items-center space-x-1">
                  {is_flagged && (
                    <Icon name="Flag" className="text-error h-3 w-3" />
                  )}
                  {group_rank && (
                    <span className="text-primary-foreground/75 text-xs">
                      #{group_rank}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (role === "system") {
      return (
        <div key={id} className="mb-2 flex justify-center px-4">
          <div className="max-w-xs lg:max-w-md">
            <div className="bg-card text-muted-foreground rounded-lg px-3 py-2 text-center">
              <div className="mb-1 flex items-center justify-center space-x-2">
                <Icon
                  name="Settings"
                  className="text-muted-foreground h-3 w-3"
                />
                <div
                  className="text-xs leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formattedContent }}
                />
              </div>
              <span className="text-muted-foreground mt-1 block text-xs">
                {timestamp}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (role === "tool") {
      const parsed = parseToolMessage(content);
      const isSuccess = parsed.type === "success";
      const isError = parsed.type === "error";

      return (
        <div key={id} className="mb-2 flex justify-center px-4">
          <div
            className={`max-w-xs rounded-lg border px-4 py-2 lg:max-w-md ${
              isSuccess
                ? "border-success/50 bg-success-bg text-success"
                : isError
                  ? "border-error/50 bg-error-bg text-error"
                  : "border-primary/50 bg-accent text-primary"
            }`}
          >
            <div className="flex items-start space-x-2">
              <Icon
                name={
                  isSuccess ? "CheckCircle" : isError ? "XCircle" : "Wrench"
                }
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
              <div className="flex-1">
                {/* {Object.keys(parsed.attributes).length > 0 && (
                  <div className="mb-2 text-xs opacity-75 space-y-1">
                    {Object.entries(parsed.attributes).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-semibold">{key}:</span> {value}
                      </div>
                    ))}
                  </div>
                )} */}
                <p className="text-sm whitespace-pre-wrap">{parsed.message}</p>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-xs opacity-75">{timestamp}</span>
                  {group_rank && (
                    <span className="text-xs opacity-50">#{group_rank}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (messagesLoading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
        <span className="text-muted-foreground ml-2">
          {t("chat.loadingMessages")}
        </span>
      </div>
    );
  }

  if (messagesError) {
    return (
      <div className="py-8 text-center">
        <Icon
          name="AlertTriangle"
          className="text-error mx-auto mb-4 h-12 w-12"
        />
        <h3 className="text-error mb-2 text-lg font-medium">
          {t("chat.errors.loadTitle")}
        </h3>
        <p className="text-muted-foreground">
          {messagesError.message || t("chat.errors.loadDescription")}
        </p>
      </div>
    );
  }

  if (allMessages.length === 0) {
    return (
      <div className="py-8 text-center">
        <Icon
          name="MessageCircle"
          className="text-muted-foreground mx-auto mb-4 h-12 w-12"
        />
        <h3 className="text-foreground mb-2 text-lg font-medium">
          {t("chat.empty.title")}
        </h3>
        <p className="text-muted-foreground">{t("chat.empty.description")}</p>
      </div>
    );
  }

  return (
    <div
      className={`border-border bg-card relative rounded-lg border ${className}`}
    >
      <div
        ref={messagesContainerCallbackRef}
        className={`${maxHeight} bg-background overflow-y-auto py-4`}
        style={{ scrollBehavior: "smooth" }}
      >
        {isLoadingMore && (
          <div className="flex items-center justify-center py-4">
            <div className="border-primary h-6 w-6 animate-spin rounded-full border-b-2"></div>
            <span className="text-muted-foreground ml-2 text-sm">
              {t("chat.loadingOlderMessages")}
            </span>
          </div>
        )}
        <div className="space-y-1">
          {allMessages
            .filter((m) => m.message)
            .map((message) => renderMessage(message))}
        </div>
        <div ref={messagesEndRef} />
      </div>
      {showScrollButton && showScrollButtonState && (
        <Button
          variant="ghost"
          onClick={() => scrollToBottom(true)}
          className="bg-primary text-primary-foreground hover:bg-primary absolute right-4 bottom-4 rounded-full p-3 shadow-lg"
          title={t("chat.scrollToBottom")}
        >
          <Icon name="ChevronDown" className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};

export default ConversationChat;
